/**
 * DownloadMarketingPage — hämtningssidan för skrivbordsapparna.
 *
 * URL: totodo.io/download
 *
 * Ligger utanför auth-grinden, som de andra marknadsföringssidorna.
 *
 * Filerna listas från R2 i stället för att skrivas in här: bygget lägger en
 * `latest.json` bredvid installationsfilerna, så en ny version dyker upp här
 * utan att någon rör den här filen. Repot är privat, så GitHub Releases går
 * inte att läsa från en publik sida — R2 är det som är öppet.
 */

import { useEffect, useMemo, useState } from "react"
import { MarketingNavigation } from "../components/MarketingNavigation"
import { MarketingBackgroundVector } from "../components/MarketingBackgroundVector"
import "../marketing.css"

const DOWNLOAD_BASE = "https://downloads.totodo.io"
const LATEST_URL = `${DOWNLOAD_BASE}/downloads/totodo/latest.json`

type Plattform =
  | "macos-arm64"
  | "macos-x64"
  | "windows-msi"
  | "windows-exe"
  | "linux-appimage"
  | "linux-deb"
  | "okand"

interface Fil {
  plattform: Plattform
  namn: string
  storlek: number
  url: string
}

interface Latest {
  version: string
  utgiven: string
  filer: Fil[]
}

/** Vad vi kallar varje nedladdning, och vem den är till för. */
const ETIKETTER: Record<Plattform, { titel: string; hint: string; os: Os }> = {
  "macos-arm64": { titel: "macOS", hint: "Apple Silicon (M1 och senare)", os: "mac" },
  "macos-x64": { titel: "macOS", hint: "Intel", os: "mac" },
  "windows-exe": { titel: "Windows", hint: "Installationsprogram", os: "windows" },
  "windows-msi": { titel: "Windows", hint: "MSI, för utrullning i företag", os: "windows" },
  "linux-appimage": { titel: "Linux", hint: "AppImage, kör på de flesta distributioner", os: "linux" },
  "linux-deb": { titel: "Linux", hint: "Debian och Ubuntu", os: "linux" },
  okand: { titel: "Övrigt", hint: "", os: "okand" },
}

type Os = "mac" | "windows" | "linux" | "okand"

/**
 * Gissa besökarens plattform. Bara för att lyfta fram rätt knapp — alla
 * nedladdningar visas ändå, så en felgissning kostar ingenting.
 */
function gissaOs(): Os {
  if (typeof navigator === "undefined") return "okand"
  const p = `${navigator.platform || ""} ${navigator.userAgent || ""}`.toLowerCase()
  if (p.includes("mac")) return "mac"
  if (p.includes("win")) return "windows"
  if (p.includes("linux") || p.includes("x11")) return "linux"
  return "okand"
}

function formateraStorlek(bytes: number): string {
  const mb = bytes / (1024 * 1024)
  return `${mb.toFixed(1)} MB`
}

function formateraDatum(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ""
  return d.toLocaleDateString("sv-SE", { year: "numeric", month: "long", day: "numeric" })
}

export function DownloadMarketingPage() {
  const [latest, setLatest] = useState<Latest | null>(null)
  const [fel, setFel] = useState(false)
  const os = useMemo(gissaOs, [])

  useEffect(() => {
    let avbruten = false
    fetch(LATEST_URL, { cache: "no-cache" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d: Latest) => {
        if (!avbruten) setLatest(d)
      })
      .catch(() => {
        if (!avbruten) setFel(true)
      })
    return () => {
      avbruten = true
    }
  }, [])

  // Filer för besökarens eget system först, resten under "Alla nedladdningar".
  const { mina, ovriga } = useMemo(() => {
    const filer = latest?.filer ?? []
    return {
      mina: filer.filter((f) => ETIKETTER[f.plattform]?.os === os),
      ovriga: filer.filter((f) => ETIKETTER[f.plattform]?.os !== os),
    }
  }, [latest, os])

  return (
    <div className="mk-page">
      <div className="mk-page-gradient">
        <MarketingBackgroundVector />
        <MarketingNavigation activePage="Download" />

        <div className="mk-header mk-header-transparent">
          <div className="mk-header-content">
            <div
              className="mk-container mk-text-center"
              style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
            >
              <h1 className="mk-title">
                Hämta <span className="mk-title-accent">To To-Do</span>
              </h1>
              <p className="mk-subtitle">
                Samma app på skrivbordet som i telefonen och i webbläsaren. Dina uppgifter
                synkas automatiskt.
              </p>

              {latest && (
                <p style={{ marginTop: 8, opacity: 0.7, fontSize: 14 }}>
                  Version {latest.version}
                  {formateraDatum(latest.utgiven) && ` · ${formateraDatum(latest.utgiven)}`}
                </p>
              )}

              <div style={{ marginTop: 40, marginBottom: 80, width: "100%", maxWidth: 560 }}>
                {fel && (
                  <p style={{ opacity: 0.8 }}>
                    Nedladdningarna går inte att lista just nu.{" "}
                    <a href="mailto:support@totodo.io" style={{ textDecoration: "underline" }}>
                      Hör av dig
                    </a>{" "}
                    så skickar vi en länk.
                  </p>
                )}

                {!latest && !fel && <p style={{ opacity: 0.6 }}>Hämtar…</p>}

                {mina.map((f) => (
                  <Knapp key={f.namn} fil={f} primar />
                ))}

                {ovriga.length > 0 && (
                  <>
                    <p style={{ marginTop: 36, marginBottom: 12, opacity: 0.6, fontSize: 14 }}>
                      {mina.length > 0 ? "Andra system" : "Alla nedladdningar"}
                    </p>
                    {ovriga.map((f) => (
                      <Knapp key={f.namn} fil={f} />
                    ))}
                  </>
                )}
              </div>

              {/* Gatekeeper slapper igenom appen utan varning eftersom den ar
                  notariserad hos Apple. Det ar vart att saga - manga vantar sig
                  en varning nar de laddar ner utanfor App Store. */}
              {os === "mac" && latest && (
                <p style={{ maxWidth: 520, opacity: 0.6, fontSize: 14, marginBottom: 60 }}>
                  Appen är signerad och notariserad hos Apple, så macOS öppnar den utan
                  varning.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function Knapp({ fil, primar = false }: { fil: Fil; primar?: boolean }) {
  const etikett = ETIKETTER[fil.plattform] ?? ETIKETTER.okand
  return (
    <a
      href={`${DOWNLOAD_BASE}/${fil.url}`}
      className={`mk-cta ${primar ? "mk-cta-primary" : "mk-cta-secondary"}`}
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        marginBottom: 12,
        gap: 16,
      }}
      download
    >
      <span style={{ textAlign: "left" }}>
        <strong>{etikett.titel}</strong>
        {etikett.hint && (
          <span style={{ display: "block", fontSize: 13, opacity: 0.75 }}>{etikett.hint}</span>
        )}
      </span>
      <span style={{ fontSize: 13, opacity: 0.75, whiteSpace: "nowrap" }}>
        {formateraStorlek(fil.storlek)}
      </span>
    </a>
  )
}
